import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { newId, useOutbox } from '@dia/db';
import { useT } from '@dia/i18n';
import { Button, cn } from '@dia/ui';
import { RequireAuth, type Identity } from './lib/identity';
import { loadJobCatalog, loadRuns, useLoad } from './lib/data';
import { runPhase } from './lib/checklist';
import { Card, Notice, Page, Pill } from './lib/ui';

/**
 * Limpieza: pick a job. The house and the list are chosen from what is on the
 * phone, and starting one works with no signal at all: the run gets its id
 * here, on the phone, and rides the outbox like every other write.
 */
export function Limpieza() {
  return <RequireAuth>{(who) => <LimpiezaBody who={who} />}</RequireAuth>;
}

function LimpiezaBody({ who }: { who: Identity }) {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const { enqueue, pending } = useOutbox();
  const catalog = useLoad(loadJobCatalog, 'catalog');
  const runs = useLoad(loadRuns, `runs${pending}`, 60_000);
  const [houseId, setHouseId] = useState<string | null>(null);
  const [listId, setListId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const houses = catalog.data?.houses ?? [];
  const lists = catalog.data?.checklists ?? [];
  const mine = (runs.data ?? []).filter(
    (r) => (r.assigned_to === who.staffId || r.assigned_to === null) && r.review_status !== 'approved',
  );

  const houseName = (id: string | null) => {
    const house = houses.find((h) => h.id === id);
    return house ? (lang === 'es' ? house.name_es : house.name_en) : t('ops.cleaning.house_wide');
  };
  const listName = (id: string) => {
    const list = lists.find((c) => c.id === id);
    return list ? (lang === 'es' ? list.name_es : list.name_en) : '';
  };

  const start = async () => {
    if (!houseId || !listId) return;
    setBusy(true);
    try {
      // The row's id and its client_id are the same value, so run_items can
      // point at the run before the island has even seen it.
      const id = newId();
      await enqueue({
        id,
        table: 'checklist_runs',
        payload: {
          id,
          checklist_id: listId,
          house_id: houseId,
          assigned_to: who.staffId,
          started_at: new Date().toISOString(),
        },
      });
      navigate(`/limpieza/${id}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title={t('ops.nav.cleaning')}>
      <div className="flex flex-col gap-5">
        <Card>
          <h2 className="mb-2 text-[1.15em] font-semibold">{t('ops.cleaning.my_jobs')}</h2>
          {mine.length === 0 ? (
            <p className="text-inkMuted">{t('ops.cleaning.no_jobs')}</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {mine.map((run) => {
                const phase = runPhase(run);
                return (
                  <li key={run.id}>
                    <Link
                      to={`/limpieza/${run.id}`}
                      className="flex min-h-tap flex-col gap-1 rounded border border-line p-3 text-ink no-underline"
                    >
                      <span className="font-semibold">{houseName(run.house_id)}</span>
                      <span className="text-inkMuted">{listName(run.checklist_id)}</span>
                      {/* Wrapping at 200 percent zoom, where the pill and the
                          word do not fit on one line (defect D9). */}
                      <span className="flex flex-wrap items-center gap-2">
                        <Pill tone={phase === 'redo' ? 'danger' : phase === 'waiting' ? 'info' : 'warn'}>
                          {t(`ops.phase.${phase}`)}
                        </Pill>
                        <span className="text-accent underline underline-offset-4">{t('ops.cleaning.continue')}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card>
          <h2 className="mb-3 text-[1.15em] font-semibold">{t('ops.cleaning.start_new')}</h2>
          {houses.length === 0 || lists.length === 0 ? (
            <Notice tone="warn">{t('ops.cleaning.no_catalog')}</Notice>
          ) : (
            <div className="flex flex-col gap-5">
              <fieldset className="m-0 border-0 p-0">
                <legend className="mb-2 font-medium">{t('ops.cleaning.pick_house')}</legend>
                <div className="flex flex-col gap-2">
                  {houses.map((house) => (
                    <Choice key={house.id} on={houseId === house.id} onClick={() => setHouseId(house.id)}>
                      {lang === 'es' ? house.name_es : house.name_en}
                    </Choice>
                  ))}
                </div>
              </fieldset>
              <fieldset className="m-0 border-0 p-0">
                <legend className="mb-2 font-medium">{t('ops.cleaning.pick_list')}</legend>
                <div className="flex flex-col gap-2">
                  {lists.map((list) => (
                    <Choice key={list.id} on={listId === list.id} onClick={() => setListId(list.id)}>
                      {lang === 'es' ? list.name_es : list.name_en}
                    </Choice>
                  ))}
                </div>
              </fieldset>
              <Button size="lg" block disabled={!houseId || !listId} busy={busy} busyLabel={t('ops.cleaning.checking_in')} onClick={() => void start()}>
                {t('ops.cleaning.start')}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </Page>
  );
}

function Choice({ on, onClick, children }: { on: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'min-h-tap rounded border px-4 py-3 text-left text-[18px]',
        on ? 'border-accent bg-accent font-semibold text-onAccent' : 'border-line bg-surface text-ink',
      )}
    >
      {on ? '✓ ' : ''}
      {children}
    </button>
  );
}
